import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ImageDropzone } from "@/components/ImageDropzone";
import { Loader2 } from "lucide-react";

const EMPTY = {
  nama_mitra: "",
  nama_ktp: "",
  no_ktp: "",
  status: "ACTIVE",
  keterangan: "",
  foto_profil: null,
  foto_profil_key: null,
  foto_ktp: null,
  foto_ktp_key: null,
};

const TEXT_FIELDS = [
  ["nama_mitra", "Nama Mitra", "Contoh: ACO"],
  ["nama_ktp", "Nama KTP", "Nama sesuai KTP"],
  ["no_ktp", "No KTP", "16 digit NIK"],
];

export const MitraEkstraDialog = ({ open, onOpenChange, editing, onSaved }) => {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(editing ? { ...EMPTY, ...editing } : EMPTY);
  }, [open, editing]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setPhoto = (field) => (val) =>
    setForm((f) => ({ ...f, [field]: val?.url || null, [`${field}_key`]: val?.key || null }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { ...form };
      delete body.id;
      delete body.created_at;
      if (editing) await api.put(`/mitra-ekstra/${editing.id}`, body);
      else await api.post("/mitra-ekstra", body);
      toast.success(editing ? "Mitra diperbarui" : "Mitra ditambahkan");
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl" data-testid="mitra-ekstra-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit Mitra Ekstra" : "Tambah Mitra Baru"}</DialogTitle>
          <DialogDescription>Mitra akan disimpan pada cabang "Ekstra".</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="mitra-ekstra-form">
          <div className="grid gap-4 sm:grid-cols-2">
            {TEXT_FIELDS.map(([k, label, ph]) => (
              <div key={k} className="space-y-1.5">
                <Label htmlFor={`me-${k}`}>{label}</Label>
                <Input
                  id={`me-${k}`}
                  value={form[k] || ""}
                  onChange={(e) => set(k, e.target.value)}
                  placeholder={ph}
                  required
                  data-testid={`mitra-ekstra-${k}-input`}
                />
              </div>
            ))}
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="mitra-ekstra-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {["ACTIVE", "INACTIVE", "BLACKLIST"].map((s) => (
                    <SelectItem key={s} value={s} data-testid={`mitra-ekstra-status-${s}`}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="me-keterangan">Keterangan</Label>
            <Textarea
              id="me-keterangan"
              rows={2}
              value={form.keterangan || ""}
              onChange={(e) => set("keterangan", e.target.value)}
              data-testid="mitra-ekstra-keterangan-input"
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <ImageDropzone label="Foto Profil" value={form.foto_profil} onChange={setPhoto("foto_profil")} testid="mitra-ekstra-foto-profil" />
            <ImageDropzone label="Foto KTP" value={form.foto_ktp} onChange={setPhoto("foto_ktp")} testid="mitra-ekstra-foto-ktp" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="mitra-ekstra-cancel-button">
              Batal
            </Button>
            <Button type="submit" disabled={saving} data-testid="mitra-ekstra-save-button">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Simpan
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
